import { useTranslation } from "react-i18next";
import Countdown from "react-countdown";
import { Loader2, QrCodeIcon } from "lucide-react";
import { Button } from "./ui/button";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter } from "./ui/dialog";
import { useGetFilesUploadQRCodeQuery } from "@/services/api.file-upload";

import { socketManager } from "@/services/websocket/socket-manager";
import {
  SOCKET_NAME_SPACES,
  SOCKET_SESSION_EVENTS,
} from "@/services/websocket/socket.enum";
import { DialogTitle } from "@radix-ui/react-dialog";
import { VisuallyHidden } from "radix-ui";

type TUploadedFileQRCodePayload = {
  fileIds: {
    frontSideFileId?: number;
    backSideFileId?: number;
  };
  fileUrls: {
    frontSideFileUrl?: string;
    backSideFileUrl?: string;
  };
};

interface IQRCodeUploadProps {
  fileType: string;
  moduleType: string;
  onUploadComplete: (data: TUploadedFileQRCodePayload) => void;
}
const UploadViaQRCode = ({
  fileType,
  moduleType,
  onUploadComplete,
}: IQRCodeUploadProps) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const { data, isLoading, error } = useGetFilesUploadQRCodeQuery(
    { fileType, moduleType },
    { skip: !isOpen || !fileType || !moduleType },
  );

  const handleUploadedFileQRCode = (payload: TUploadedFileQRCodePayload) => {
    onUploadComplete(payload);
    setIsOpen(false);
  };

  useEffect(() => {
    if (data && data.success) {
      socketManager.on(
        SOCKET_NAME_SPACES.SESSIONS,
        SOCKET_SESSION_EVENTS.UPLOADED_FILE_QR,
        (payload: TUploadedFileQRCodePayload) =>
          handleUploadedFileQRCode(payload),
      );
    }
    return () => {
      socketManager.off(
        SOCKET_NAME_SPACES.SESSIONS,
        SOCKET_SESSION_EVENTS.UPLOADED_FILE_QR,
        handleUploadedFileQRCode,
      );
    };
  }, [data]);

  const renderer = ({
    minutes,
    seconds,
    completed,
  }: {
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) return <span>{t("common:qr_upload.expired")}</span>;

    return (
      <span>
        {t("common:qr_upload.expires_in", {
          time: `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
        })}
      </span>
    );
  };

  return (
    <>
      <div className="rounded-sm border border-info hover:bg-primary-muted w-full border-dashed p-1.5 bg-primary-muted">
        <button
          type="button"
          className="bg-white w-full  flex items-center justify-center gap-2 p-2 rounded-xs text-primary"
          onClick={() => setIsOpen(true)}
        >
          <QrCodeIcon className="w-4 h-4" />
          {t("common:shared_ui.upload_via_qr_code")}
        </button>
      </div>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <div className="text-center flex gap-2 items-center justify-center">
            <QrCodeIcon className="w-6 h-6 text-primary" />
            <VisuallyHidden.Root>
              <DialogTitle>
                {t("common:shared_ui.upload_via_phone")}
              </DialogTitle>
            </VisuallyHidden.Root>
            <p className="text-lg font-bold">
              {t("common:shared_ui.upload_via_phone")}
            </p>
          </div>
          {isLoading && (
            <div className="flex items-center justify-center gap-2 w-96 h-96">
              <Loader2 className="w-4 h-4 animate-spin" />
              <p className="text-sm text-gray-500">
                {t("common:shared_ui.loading_qr_code")}
              </p>
            </div>
          )}
          {error && (
            <div className="flex items-center justify-center w-96 h-96">
              <p className="text-sm text-danger">
                {t("common:shared_ui.failed_to_generate_qr_code")}
              </p>
            </div>
          )}
          {data && data.success && (
            <div className="text-center flex flex-col gap-2 items-center justify-center">
              <div className="flex flex-col gap-2 items-center justify-center w-[228px] h-[228px] p-1 rounded-xl bg-white">
                <img
                  src={data.data.qrCodeDataUrl}
                  alt={t("common:shared_ui.qr_code_alt")}
                  className="w-full object-contain"
                />
              </div>
              <p className="text-sm text-gray-500">
                {t("common:shared_ui.scan_qr_upload_hint")}
              </p>
              <p className="text-sm text-warning bg-warning-muted px-2 py-1 rounded-md">
                <Countdown
                  date={new Date(data.data.expiredAt)}
                  renderer={renderer}
                />
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setIsOpen(false)}
            >
              {t("common:buttons.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UploadViaQRCode;
